import {OnClickReload} from "components/src/common/ReloadButton";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {createContext, useCallback, useEffect, useMemo, useRef} from 'react'

interface IPageReloadContext {
    /**
     * Makes the handler the reload handler of the current page. Returns a function which removes it again, but only
     * while it is still the current one, so a header which unmounts later cannot remove the handler of another.
     */
    setReloadHandler: (handler: OnClickReload) => () => void
    /**
     * Reloads the current page, as its reload button does. Does nothing when the page has no reload handler, or while
     * the spinner shows, when the reload button cannot be clicked either.
     */
    reloadCurrentPage: () => void
}

export const PageReloadContext = createContext<IPageReloadContext>({
    setReloadHandler: () => () => {},
    reloadCurrentPage: () => {},
});

interface PageReloadContextProviderProps {
    children: React.ReactNode;
}

/**
 * A context provider component which lets the sidebar reload the current page when its menu item, which is already
 * active, is clicked. The page header registers the reload handler of the page. A popup with a sidebar of its own,
 * such as the settings of a user or domain, has a provider of its own, so its items do not reload the page behind it.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The children components.
 *
 * @returns {React.ReactNode} - The rendered React node.
 */
export function PageReloadContextProvider({children} : Readonly<PageReloadContextProviderProps>)
{
    // a ref and not state: registering a handler must not re-render the layout
    const reloadHandler = useRef<OnClickReload | undefined>(undefined);

    const setReloadHandler = useCallback((handler: OnClickReload) => {
        reloadHandler.current = handler;

        return () => {
            if (reloadHandler.current === handler) {
                reloadHandler.current = undefined;
            }
        };
    }, []);

    // a ref as well, so the context value stays the same and the sidebar items do not re-render with the spinner
    const {spinnerStateCounter} = useSpinnerStatusContext();
    const spinnerStateCounterRef = useRef(spinnerStateCounter);

    useEffect(() => {
        spinnerStateCounterRef.current = spinnerStateCounter;
    }, [spinnerStateCounter]);

    const reloadCurrentPage = useCallback(() => {
        if (spinnerStateCounterRef.current === 0) {
            reloadHandler.current?.();
        }
    }, []);

    const exposedData = useMemo<IPageReloadContext>(() => ({
        setReloadHandler : setReloadHandler,
        reloadCurrentPage : reloadCurrentPage,
    }), [setReloadHandler, reloadCurrentPage]);

    return(
        <PageReloadContext.Provider value = {exposedData}>
            {children}
        </PageReloadContext.Provider>
    )
}
